import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { DataPersistence } from '@nrwl/angular';
import { ApiService } from '@quorum/api';
import { ProspectQueryParameters } from '@quorum/models/xs-query';
import { SALES_PLANNER_USER_SETTINGS_CATEGORY } from '@quorum/models/xs-resource';
import { from, of } from 'rxjs';
import { map, mergeAll, tap } from 'rxjs/operators';
import * as fromSalesPlannerLegacy from '../salesplanner-legacy.actions';
import * as fromLocalSettings from './local-settings.actions';
import { buildSalesPlannerUserSettingsLoadId, resolveUserId } from './local-settings.effects.helpers';
import { LocalSettingsState } from './local-settings.interfaces';
import { LocalSettingsStateService } from './local-settings.state.service';
import {
  fillDefaultsIntoState,
  loadAffectingSettingsDiffer,
  mergePersistedIntoState,
  PersistedSalesPlannerSettings,
} from './local-settings.util';

const SETTINGS_URL = `v/1/system/settings/user/${SALES_PLANNER_USER_SETTINGS_CATEGORY}`;

interface UserSettingsResponse {
  destinationId: string;
  userId: string;
  category: string;
  recordExists?: boolean;
  schemaVersion: number;
  settings: Partial<PersistedSalesPlannerSettings>;
}


@Injectable()
export class LocalSettingsEffects {
  loadSalesPlannerUserSettings = createEffect(() =>
    this.dataPersistence.fetch(fromLocalSettings.GET_SALES_PLANNER_USER_SETTINGS_FROM_SERVER, {
      // Scope the dedup key to the resolved userId. A constant id would group every load under one
      // bucket, which means a rapid logout/login or user-switch could conflate the two users'
      // requests. With a per-user id, the same user retrying inside a session still benefits from
      // dedup, but a different user's load runs independently.
      id: (action: fromLocalSettings.GetSalesPlannerUserSettingsFromServer, state: any) =>
        buildSalesPlannerUserSettingsLoadId(action.payload && action.payload.userId, state),
      run: (action: fromLocalSettings.GetSalesPlannerUserSettingsFromServer, state: any) => {
        const userId = resolveUserId(action.payload && action.payload.userId, state);
        if (!userId) {
          return of([]).pipe(mergeAll());
        }
        return this.apiService
          .get<UserSettingsResponse>(SETTINGS_URL, { params: { userId } })
          .pipe(
            map((response) => {
              const persisted = (response && response.settings) || {};
              const recordExists = !!(response && response.recordExists);
              return new fromLocalSettings.GetSalesPlannerUserSettingsFromServerSuccess({
                recordExists,
                settings: persisted,
              });
            })
          );
      },
      onError: (_action: fromLocalSettings.GetSalesPlannerUserSettingsFromServer, error: any) => {
        console.error('Error loading sales planner user settings', error);
      },
    })
  );

  applyLoadedSalesPlannerUserSettings = createEffect(() =>
    this.dataPersistence.fetch(fromLocalSettings.GET_SALES_PLANNER_USER_SETTINGS_FROM_SERVER_SUCCESS, {
      run: (action: fromLocalSettings.GetSalesPlannerUserSettingsFromServerSuccess, state: any) => {
        if (!action.payload) {
          return from([]);
        }
        const currentLocalSettings: LocalSettingsState =
          state && state.salesplannerLegacy && state.salesplannerLegacy.localSettings;
        if (!currentLocalSettings) {
          return from([]);
        }
        // recordExists=true: server is the source of truth; persisted values overlay current.
        // recordExists=false: keep current values (incl. seeds like `salesPeople: [associateId]`),
        //   but fill in any missing nested defaults so legacy localStorage hydrations pick up
        //   newly-added subkeys (e.g. a future accessibility flag) without waiting for a manual save.
        const next = action.payload.recordExists
          ? mergePersistedIntoState(currentLocalSettings, action.payload.settings)
          : fillDefaultsIntoState(currentLocalSettings);

        const dispatched: Action[] = [new fromLocalSettings.UpdateLocalSettingsInState(next)];

        // Race-condition fix: when the navigation effect in salesplanner-legacy.effects.ts ran
        // before the GET response arrived (`featureLoaded === true`), it already fired the initial
        // activities and prospects loads using whatever `localSettings` was in state at that
        // moment (localStorage hydrate + `[associateId]` seed). Now that we have the user's saved
        // values we need to re-issue those loads so the UI matches their persisted preferences
        // instead of stale filters. Only relevant when there really was a stored record; the
        // recordExists=false branch produced no semantic change to active filter values.
        //
        // We dispatch a single intra-lib action and let salesplanner-legacy.effects.ts (which
        // already imports `fromProspects` and `fromActivities`) do the actual reload dispatches.
        // This keeps THIS file from acquiring a new edge into `@quorum/xsr-entities/state`.
        //
        // Skip the dispatch when the merged settings are equivalent to what was already in state
        // for the load-affecting fields — that's the common "same device, localStorage matches
        // server" case, and re-fetching there would just churn for no UI change.
        if (
          action.payload.recordExists &&
          state.salesplannerLegacy.featureLoaded &&
          loadAffectingSettingsDiffer(currentLocalSettings, next)
        ) {
          const employeeId = Array.isArray(next.salesPeople) ? next.salesPeople.join(',') : next.salesPeople;
          let prospectQueryParameters = new ProspectQueryParameters({
            employeeId,
            statusId: next.prospectFilter.status,
            typeId: next.prospectFilter.typeId,
            page: 0,
            pageSize: 50,
            sort: next.prospectFilter.sort,
            embed: ['associate', 'primaryEmployeeAssociate', 'vehicle', 'address'],
          });
          // Mirror the navigation effect's conditional channel/source inclusion (see
          // salesplanner-legacy.effects.ts:133-139). Empty arrays must NOT be sent or the API
          // returns nothing; presence of either filter narrows the result set.
          if (next.prospectFilter.channel && next.prospectFilter.channel.length > 0) {
            prospectQueryParameters = { ...prospectQueryParameters, channel: next.prospectFilter.channel };
          }
          if (next.prospectFilter.source && next.prospectFilter.source.length > 0) {
            prospectQueryParameters = { ...prospectQueryParameters, source: next.prospectFilter.source };
          }
          dispatched.push(
            new fromSalesPlannerLegacy.RefreshDatasetsForAppliedSettings({
              localSettings: next,
              prospectQueryParameters,
            })
          );
        }

        return from(dispatched);
      },
      onError: (_action: fromLocalSettings.GetSalesPlannerUserSettingsFromServerSuccess, error: any) => {
        console.error('Error applying loaded sales planner user settings', error);
      },
    })
  );

  saveSalesPlannerUserSettings = createEffect(() =>
    this.dataPersistence.fetch(fromLocalSettings.SAVE_SALES_PLANNER_USER_SETTINGS_TO_SERVER, {
      run: (action: fromLocalSettings.SaveSalesPlannerUserSettingsToServer, state: any) => {
        const userId = resolveUserId(undefined, state);
        if (!userId) {
          return new fromLocalSettings.SaveSalesPlannerUserSettingsToServerFailure({
            error: new Error('Cannot resolve userId; not authenticated.'),
            settings: action.payload,
          });
        }
        return this.apiService
          .put<UserSettingsResponse>(SETTINGS_URL, { settings: action.payload }, { params: { userId } })
          .pipe(map(() => new fromLocalSettings.SaveSalesPlannerUserSettingsToServerSuccess(action.payload)));
      },
      onError: (action: fromLocalSettings.SaveSalesPlannerUserSettingsToServer, error: any) => {
        console.error('Error saving sales planner user settings', error);
        return new fromLocalSettings.SaveSalesPlannerUserSettingsToServerFailure({
          error,
          settings: action.payload,
        });
      },
    })
  );

  notifySaveSalesPlannerUserSettingsSuccess = createEffect(
    () =>
      this.actions.pipe(
        ofType<fromLocalSettings.SaveSalesPlannerUserSettingsToServerSuccess>(
          fromLocalSettings.SAVE_SALES_PLANNER_USER_SETTINGS_TO_SERVER_SUCCESS
        ),
        tap(() => {
          this.localSettingsStateService.addToast({
            message: 'Sales Planner settings saved.',
            actionText: 'Dismiss',
            options: { duration: 3000 },
          });
        })
      ),
    { dispatch: false }
  );

  notifySaveSalesPlannerUserSettingsFailure = createEffect(
    () =>
      this.actions.pipe(
        ofType<fromLocalSettings.SaveSalesPlannerUserSettingsToServerFailure>(
          fromLocalSettings.SAVE_SALES_PLANNER_USER_SETTINGS_TO_SERVER_FAILURE
        ),
        tap(() => {
          this.localSettingsStateService.addToast({
            message:
              'We could not save your Sales Planner settings. Your changes are visible now but may revert next sign-in. Please try again.',
            actionText: 'Dismiss',
            options: { duration: 8000, panelClass: 'mat-snackbar-error' },
          });
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions: Actions,
    private dataPersistence: DataPersistence<any>,
    private apiService: ApiService,
    private localSettingsStateService: LocalSettingsStateService
  ) {}
}
